import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiFehler, dokumentHochladen } from "../api/client";
import { schluessel, useAnlagen, useExtraktionWiederholen } from "../api/hooks";
import type { Dokument, DokumentTyp } from "../api/typen";
import { Hinweis, Karte, Knopf, Spinner } from "../komponenten/Basis";
import { DokumentVorschau, Thumbnail } from "../komponenten/DokumentVorschau";
import { ExtraktionsZusammenfassung, FeldListe, VollmachtChecks } from "../komponenten/Extraktion";
import { KonfidenzLegende } from "../komponenten/Konfidenz";
import { KundeAuswahl } from "../komponenten/KundeAuswahl";
import { UploadZone } from "../komponenten/UploadZone";
import { useToast } from "../kontext/ToastKontext";
import { EXTRAKTION_TEXT, STATUS_TEXT, TYP_ICON, TYP_TEXT, dateigroesse, kwp } from "../lib/format";

const MB = 1024 * 1024;

type Phase = "wartend" | "upload" | "extraktion" | "fertig" | "fehler";

interface UploadEintrag {
  id: string;
  datei: File;
  typ: DokumentTyp;
  kundeId: number;
  anlageId: number | null;
  phase: Phase;
  fortschritt: number;
  dokument?: Dokument;
  fehler?: ApiFehler;
  abbrechen?: () => void;
}

function PhaseAnzeige({ eintrag }: { eintrag: UploadEintrag }) {
  switch (eintrag.phase) {
    case "wartend":
      return <span className="text-xs text-neutral-500">Wartet…</span>;
    case "upload":
      return (
        <div className="w-full">
          <div className="h-1.5 w-full overflow-hidden rounded bg-neutral-200">
            <div className="h-full bg-neutral-900 transition-all" style={{ width: `${eintrag.fortschritt}%` }} />
          </div>
          <span className="text-xs text-neutral-500">Hochladen… {eintrag.fortschritt} %</span>
        </div>
      );
    case "extraktion":
      return <Spinner klein label={eintrag.typ === "foto" ? "Speichere…" : "Extrahiere Daten…"} />;
    case "fehler":
      return <span className="text-xs font-semibold text-ampel-rot">✗ {eintrag.fehler?.message}</span>;
    case "fertig": {
      const d = eintrag.dokument;
      if (!d) return null;
      const farbe = d.extraktion_status === "fehlgeschlagen" ? "text-ampel-rot" : "text-ampel-gruen";
      return <span className={`text-xs font-semibold ${farbe}`}>{d.extraktion_status === "fehlgeschlagen" ? "⚠" : "✓"} {EXTRAKTION_TEXT[d.extraktion_status]}</span>;
    }
  }
}

export function UploadSeite() {
  const [parameter, setParameter] = useSearchParams();
  const kundeId = parameter.get("kunde") ? Number(parameter.get("kunde")) : null;
  const zielAnlage = parameter.get("anlage") ? Number(parameter.get("anlage")) : null;
  const [rechnungTyp, setRechnungTyp] = useState<"rechnung" | "formular">("rechnung");
  const [eintraege, setEintraege] = useState<UploadEintrag[]>([]);
  const [ausgewaehlt, setAusgewaehlt] = useState<string | null>(null);
  const aktiv = useRef(false);
  const toast = useToast();
  const queryClient = useQueryClient();
  const wiederholen = useExtraktionWiederholen();

  const anlagen = useAnlagen({ kundeId: kundeId ?? undefined, limit: 50, offset: 0 }, Boolean(kundeId));
  const offeneAnlagen = (anlagen.data?.items ?? []).filter((a) => a.status === "draft" || a.status === "validiert");

  function setzeParameter(kunde: number | null, anlage: number | null) {
    const neu = new URLSearchParams();
    if (kunde) neu.set("kunde", String(kunde));
    if (kunde && anlage) neu.set("anlage", String(anlage));
    setParameter(neu, { replace: true });
  }

  const aktualisieren = useCallback((id: string, teil: Partial<UploadEintrag>) => {
    setEintraege((alt) => alt.map((e) => (e.id === id ? { ...e, ...teil } : e)));
  }, []);

  // Warteschlange: Dateien nacheinander hochladen (schont das Rate-Limit von 10 Uploads/Minute).
  useEffect(() => {
    if (aktiv.current) return;
    const naechster = eintraege.find((e) => e.phase === "wartend");
    if (!naechster) return;
    aktiv.current = true;
    const { ergebnis, abbrechen } = dokumentHochladen(
      { datei: naechster.datei, typ: naechster.typ, kundeId: naechster.kundeId, anlageId: naechster.anlageId },
      (prozent) => aktualisieren(naechster.id, prozent >= 100 ? { phase: "extraktion", fortschritt: 100 } : { fortschritt: prozent }),
    );
    aktualisieren(naechster.id, { phase: "upload", abbrechen });
    ergebnis
      .then((dokument) => {
        aktualisieren(naechster.id, { phase: "fertig", dokument });
        if (dokument.typ !== "foto") setAusgewaehlt(naechster.id);
        if (dokument.extraktion_status === "fehlgeschlagen") {
          toast.fehler(
            new ApiFehler(200, "extraktion", `${dokument.fehler ?? "Extraktion fehlgeschlagen"}${dokument.hinweis ? ` – ${dokument.hinweis}` : ""}`),
            `${naechster.datei.name}: gespeichert, aber nicht extrahiert`,
          );
        } else {
          toast.erfolg(`${TYP_TEXT[dokument.typ]} hochgeladen`, dokument.typ === "foto" ? naechster.datei.name : `${naechster.datei.name} – Daten extrahiert`);
        }
        void queryClient.invalidateQueries({ queryKey: schluessel.kunden });
        void queryClient.invalidateQueries({ queryKey: schluessel.anlagenAlle });
        if (dokument.anlage_id) void queryClient.invalidateQueries({ queryKey: schluessel.anlage(dokument.anlage_id) });
      })
      .catch((fehler: ApiFehler) => {
        aktualisieren(naechster.id, { phase: "fehler", fehler });
        if (fehler.code !== "abgebrochen") toast.fehler(fehler, `Upload fehlgeschlagen: ${naechster.datei.name}`);
      })
      .finally(() => {
        aktiv.current = false;
        setEintraege((alt) => [...alt]); // nächsten Eintrag anstoßen
      });
  }, [eintraege, aktualisieren, toast, queryClient]);

  function hinzufuegen(dateien: File[], typ: DokumentTyp) {
    if (!kundeId) return;
    const neu = dateien.map<UploadEintrag>((datei) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      datei,
      typ,
      kundeId,
      anlageId: zielAnlage,
      phase: "wartend",
      fortschritt: 0,
    }));
    setEintraege((alt) => [...neu, ...alt]);
  }

  function erneutExtrahieren(eintrag: UploadEintrag) {
    if (!eintrag.dokument) return;
    aktualisieren(eintrag.id, { phase: "extraktion" });
    wiederholen.mutate(eintrag.dokument.id, {
      onSuccess: (dokument) => {
        aktualisieren(eintrag.id, { phase: "fertig", dokument });
        if (dokument.extraktion_status === "erfolgreich") toast.erfolg("Extraktion erfolgreich", eintrag.datei.name);
        else toast.fehler(new ApiFehler(200, "extraktion", dokument.fehler ?? "Extraktion fehlgeschlagen"), "Erneut fehlgeschlagen");
      },
      onError: (fehler) => {
        aktualisieren(eintrag.id, { phase: "fertig" });
        toast.fehler(fehler, "Extraktion konnte nicht gestartet werden");
      },
    });
  }

  const auswahl = useMemo(() => eintraege.find((e) => e.id === ausgewaehlt && e.dokument), [eintraege, ausgewaehlt]);
  const fehlerMelden = (meldungen: string[]) => toast.fehler(new Error(meldungen.join(" · ")), "Datei abgelehnt");
  const ohneKunde = !kundeId;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Dokumente hochladen</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Rechnung/Formular und Vollmacht werden automatisch ausgelesen. Fotos werden nur gespeichert.
          </p>
        </div>
        <KonfidenzLegende />
      </div>

      <Karte className="p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <KundeAuswahl wert={kundeId} beiAuswahl={(id) => setzeParameter(id, null)} />
          <div>
            <label htmlFor="anlage" className="mb-1 block text-xs font-semibold text-neutral-700">
              Zuordnung zur Anlage
            </label>
            <select
              id="anlage"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm disabled:bg-neutral-50"
              value={zielAnlage ?? ""}
              disabled={ohneKunde}
              onChange={(e) => setzeParameter(kundeId, e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Automatisch (offene Anlage des Kunden oder neue Anlage)</option>
              {offeneAnlagen.map((a) => (
                <option key={a.id} value={a.id}>
                  Anlage #{a.id} · {STATUS_TEXT[a.status]} · {kwp(a.leistung_kwp)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-neutral-500">Bestätigte Anlagen sind gesperrt und nehmen keine Dokumente mehr an.</p>
          </div>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-3">
          <UploadZone
            titel="Rechnung / Formular"
            icon="📄"
            beschreibung="PDF, PNG, JPG oder Word (.docx, .doc) · max. 20 MB"
            erlaubt={["pdf", "png", "jpeg", "docx", "doc"]}
            maxBytes={20 * MB}
            deaktiviert={ohneKunde}
            deaktiviertHinweis="Zuerst Kunde auswählen"
            beiDateien={(d) => hinzufuegen(d, rechnungTyp)}
            beiFehler={fehlerMelden}
            kopfzeile={
              <div className="inline-flex rounded-lg bg-neutral-100 p-0.5 text-xs font-semibold" role="radiogroup" aria-label="Dokumenttyp">
                {(["rechnung", "formular"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={rechnungTyp === t}
                    onClick={() => setRechnungTyp(t)}
                    className={`rounded-md px-2.5 py-1 ${rechnungTyp === t ? "bg-white shadow-sm" : "text-neutral-500"}`}
                  >
                    {TYP_TEXT[t]}
                  </button>
                ))}
              </div>
            }
          />
          <UploadZone
            titel="Vollmacht"
            icon="✍️"
            beschreibung="PDF, PNG, JPG oder Word · max. 20 MB · Unterschrift + Datum werden geprüft (bei Word nur das Datum)"
            erlaubt={["pdf", "png", "jpeg", "docx", "doc"]}
            maxBytes={20 * MB}
            deaktiviert={ohneKunde}
            deaktiviertHinweis="Zuerst Kunde auswählen"
            beiDateien={(d) => hinzufuegen(d, "vollmacht")}
            beiFehler={fehlerMelden}
          />
          <UploadZone
            titel="Fotos (optional)"
            icon="📸"
            beschreibung="Stromlaufplan, Montagestelle · JPG oder PNG · max. 5 MB · mehrere möglich"
            erlaubt={["jpeg", "png"]}
            maxBytes={5 * MB}
            mehrere
            deaktiviert={ohneKunde}
            deaktiviertHinweis="Zuerst Kunde auswählen"
            beiDateien={(d) => hinzufuegen(d, "foto")}
            beiFehler={fehlerMelden}
          />
        </div>
      </Karte>

      {eintraege.length > 0 && (
        <Karte className="p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-neutral-500">Uploads dieser Sitzung</h2>
          <ul className="divide-y divide-neutral-100">
            {eintraege.map((e) => (
              <li key={e.id} className={`flex flex-wrap items-center gap-3 py-3 ${ausgewaehlt === e.id ? "bg-neutral-50" : ""}`}>
                {e.dokument ? <Thumbnail dokument={e.dokument} groesse="h-10 w-10" /> : <span className="w-10 text-center text-xl" aria-hidden>{TYP_ICON[e.typ]}</span>}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{e.datei.name}</p>
                  <p className="text-xs text-neutral-500">
                    {TYP_TEXT[e.typ]} · {dateigroesse(e.datei.size)}
                    {e.dokument?.anlage_id ? ` · Anlage #${e.dokument.anlage_id}` : ""}
                  </p>
                  {e.dokument && e.phase === "fertig" && <ExtraktionsZusammenfassung dokument={e.dokument} />}
                </div>
                <div className="w-52"><PhaseAnzeige eintrag={e} /></div>
                <div className="flex gap-2">
                  {e.phase === "upload" && <Knopf variante="geist" onClick={() => e.abbrechen?.()}>Abbrechen</Knopf>}
                  {e.phase === "fertig" && e.dokument?.extraktion_status === "fehlgeschlagen" && e.dokument.wiederholbar && (
                    <Knopf onClick={() => erneutExtrahieren(e)}>↻ Erneut versuchen</Knopf>
                  )}
                  {e.phase === "fertig" && e.dokument && e.dokument.typ !== "foto" && (
                    <Knopf variante="geist" onClick={() => setAusgewaehlt(e.id)}>Ansehen</Knopf>
                  )}
                  {e.phase === "fertig" && e.dokument?.anlage_id && (
                    <Link to={`/anlagen/${e.dokument.anlage_id}`} className="rounded-lg bg-marke px-3 py-2 text-sm font-semibold text-white hover:bg-marke-hover">
                      Prüfen →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Karte>
      )}

      {auswahl?.dokument && (
        <Karte className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">
              {TYP_ICON[auswahl.dokument.typ]} {auswahl.datei.name}
            </h2>
            {auswahl.dokument.anlage_id && (
              <Link to={`/anlagen/${auswahl.dokument.anlage_id}`} className="rounded-lg bg-marke px-4 py-2 text-sm font-semibold text-white hover:bg-marke-hover">
                Zur Prüfung →
              </Link>
            )}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <DokumentVorschau dokument={auswahl.dokument} />
            <div className="space-y-4">
              {auswahl.dokument.extraktion_status === "fehlgeschlagen" && (
                <Hinweis art="fehler">
                  <p className="font-semibold">{auswahl.dokument.fehler}</p>
                  {auswahl.dokument.hinweis && <p className="mt-1">{auswahl.dokument.hinweis}</p>}
                </Hinweis>
              )}
              {auswahl.dokument.typ === "vollmacht" && <VollmachtChecks dokument={auswahl.dokument} />}
              <FeldListe dokument={auswahl.dokument} />
            </div>
          </div>
        </Karte>
      )}
    </div>
  );
}
